import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import InfoButton from './InfoButton';
import { productOperationsInfo } from './operationsInfo';
import { getMissions, getMissionById, createMission, updateMission, deleteMission, productToMission, createMissionWithFiles, updateMissionWithFiles } from '../../services/adminService';
import { Mission, MissionObjectiveDefinition, MissionObjectiveType } from '../../types/operation';
import { adminColors } from '../../styles/adminStyles';
import ConfirmModal from '../common/ConfirmModal';
import { useConfirmModal } from '../../hooks/useConfirmModal';

// Стили
const Container = styled.div`
  margin-bottom: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.75rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0;
  font-weight: 700;
`;

const SearchBar = styled.div`
  display: flex;
  margin-bottom: 15px;
`;

const SearchInput = styled.input`
  flex: 1;
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  padding: 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.6);
  }
`;

const SearchButton = styled.button`
  background: rgba(0, 30, 60, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.4);
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 700;
  padding: 0 24px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
  }
`;

const DataTable = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(0, 30, 60, 0.3);
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: 60px 180px 200px 100px 100px 120px 140px;
`;

const TableHeaderRow = styled.div`
  display: contents;

  div {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 164, 255, 0.05) 100%);
    padding: 12px 15px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    color: #ffffff;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 1px solid rgba(0, 212, 255, 0.2);
    border-right: 1px solid rgba(0, 212, 255, 0.1);

    &:last-child {
      border-right: none;
    }
  }
`;

const TableDataRow = styled.div`
  display: contents;

  div {
    padding: 15px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    color: #ffffff;
    font-weight: 600;
    border-bottom: 1px solid rgba(0, 212, 255, 0.1);
    border-right: 1px solid rgba(0, 212, 255, 0.05);

    &:last-child {
      border-right: none;
    }

    &.active {
      color: #00ff88;
    }

    &.draft {
      color: #ffa500;
    }

    &.archived {
      color: #ff6666;
    }

    &.airsoft {
      color: #00d4ff;
    }

    &.paintball {
      color: #ffa500;
    }
  }

  &:hover div {
    background: rgba(0, 212, 255, 0.05);
  }

  &:last-child div {
    border-bottom: none;
  }
`;

const ActionButton = styled.button`
  background: rgba(0, 30, 60, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.4);
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  font-weight: 700;
  padding: 10px 14px;
  cursor: pointer;
  margin-right: 5px;
  border-radius: 4px;
  text-transform: uppercase;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
  }
`;

const DeleteButton = styled(ActionButton)`
  border-color: rgba(255, 100, 100, 0.5);

  &:hover {
    background: rgba(255, 100, 100, 0.2);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  margin-bottom: 15px;
  align-items: center;
`;

const FormLabel = styled.label`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff;
  font-weight: 700;
`;

const FormInput = styled.input`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;
  width: 100%;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.6);
  }
`;

const FormTextArea = styled.textarea`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.6);
  }
`;

const FormSelect = styled.select`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;
  width: 100%;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.6);
  }

  option {
    background: rgba(0, 30, 60, 1);
  }
`;

const SectionTitle = styled.h3`
  font-family: 'Courier New', monospace;
  font-size: 18px;
  color: #ffffff;
  font-weight: 700;
  text-transform: uppercase;
  margin: 25px 0 12px;
`;

const ObjectivesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ObjectiveCard = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.4);
  background: rgba(0, 30, 60, 0.35);
  padding: 12px;
  border-radius: 6px;
`;

const ObjectiveHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ObjectiveFields = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SmallInput = styled(FormInput)`
  width: 100%;
`;

const SmallSelect = styled(FormSelect)`
  width: 100%;
`;

const RemoveObjectiveButton = styled(ActionButton)`
  margin-right: 0;
`;

const AddObjectiveButton = styled(ActionButton)`
  margin-top: 6px;
  align-self: flex-start;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const SubmitButton = styled.button`
  background: rgba(0, 51, 0, 0.8);
  border: 1px solid #00cc00;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 16px;
  font-weight: 700;
  padding: 14px 28px;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: rgba(0, 102, 0, 0.5);
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
`;

const MetricCard = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.4);
  background: rgba(0, 30, 60, 0.3);
  padding: 20px;
  text-align: center;
  border-radius: 8px;
`;

const MetricLabel = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff;
  font-weight: 700;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const MetricValue = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 40px;
  color: #ffffff;
  font-weight: 700;
  margin-bottom: 10px;
`;

const MetricFooter = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff;
  font-weight: 600;
`;

const ErrorText = styled.div`
  color: #ff6666;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 15px;
  padding: 12px;
  border: 1px solid rgba(255, 100, 100, 0.5);
  background: rgba(51, 0, 0, 0.3);
  border-radius: 6px;
`;

const SuccessText = styled.div`
  color: #00ff00;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 15px;
  padding: 12px;
  border: 1px solid #00cc00;
  background: rgba(0, 51, 0, 0.3);
  border-radius: 6px;
`;

const FileInput = styled.input`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 10px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  width: 100%;
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
`;

const ImagePreview = styled.div<{ url: string }>`
  width: 150px;
  height: 150px;
  background-image: url(${props => props.url});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border: 1px solid rgba(0, 212, 255, 0.4);
  border-radius: 6px;
  position: relative;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 100, 100, 0.5);
  color: #ff6666;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 100, 100, 0.3);
  }
`;

const VideoInput = styled.input`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  padding: 10px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  width: 100%;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.6);
  }
`;

const VideoPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const VideoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(0, 30, 60, 0.3);
  border-radius: 6px;
`;

const VideoUrl = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff;
  font-weight: 600;
  flex: 1;
  margin-right: 10px;
  word-break: break-all;
`;

const RemoveVideoButton = styled.button`
  background: rgba(255, 100, 100, 0.2);
  border: 1px solid rgba(255, 100, 100, 0.5);
  color: #ff6666;
  padding: 5px 10px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 100, 100, 0.3);
  }
`;

const AddVideoButton = styled.button`
  background: rgba(0, 30, 60, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.4);
  color: #ffffff;
  padding: 8px 15px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  border-radius: 4px;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const StyledCheckbox = styled.div<{ checked: boolean }>`
  width: 20px;
  height: 20px;
  background: ${props => props.checked ? 'rgba(0, 102, 0, 0.8)' : 'rgba(0, 30, 60, 0.5)'};
  border: 1px solid ${props => props.checked ? '#00cc00' : 'rgba(0, 212, 255, 0.4)'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &::after {
    content: '${props => props.checked ? '✓' : ''}';
    color: #00cc00;
    font-size: 14px;
  }
`;

const OperationManagement: React.FC = () => {
  const { confirm, modalProps } = useConfirmModal();
  // State for loading and errors
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // State for missions
  const [missions, setMissions] = useState<Mission[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentMission, setCurrentMission] = useState<Mission | null>(null);

  // State for file uploads
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  
  // State for video management
  const [newVideoUrl, setNewVideoUrl] = useState('');
  
  // Game types
  const gameTypes = {
    AIRSOFT: "AIRSOFT" as const,
    PAINTBALL: "PAINTBALL" as const
  };
  
  // Mission statuses
  const missionStatuses = {
    ACTIVE: "ACTIVE" as const,
    DRAFT: "DRAFT" as const,
    ARCHIVED: "ARCHIVED" as const
  };
  
  // Difficulty levels
  const difficultyLevels = [
    { value: 1, label: "Easy" },
    { value: 2, label: "Medium" },
    { value: 3, label: "Hard" },
    { value: 4, label: "Expert" }
  ];
  
  // Default mission for creation
const defaultMission: Mission = {
  id: '',
  name: '',
  description: '',
  price: 0,
  gameType: gameTypes.AIRSOFT,
  difficulty: 2,
  missionId: `MSN-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
  status: missionStatuses.DRAFT,
  videos: [],
  winPoints: 0,
  losePoints: 0,
  duration: 120,
  objectives: [],
  isLiveTrackable: false,
};
  
  // Load missions on component mount and every 5 minutes
  useEffect(() => {
    fetchMissions();
    
    // Automatic update every 5 minutes
    const interval = setInterval(() => {
      fetchMissions();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Function to load missions
  const fetchMissions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await getMissions();
      setMissions(response.operations.map(productToMission));
      setTotalCount(response.totalCount);
    } catch (err) {
      setError('Failed to load missions. Please check server connection.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handler for adding a new mission
  const handleAddMission = () => {
    const freshMission: Mission = {
      ...defaultMission,
      missionId: `MSN-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      winPoints: 0,
      losePoints: 0,
      duration: 120,
      objectives: [],
    };

    setCurrentMission(freshMission);
    setSelectedFiles([]);
    setPreviewImages([]);
    setNewVideoUrl('');
    setIsModalOpen(true);
  };
  
  // Handler for editing a mission
  const createObjectiveId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `obj-${Math.random().toString(36).slice(2, 10)}`;
  };

  const handleEditMission = async (mission: Mission) => {
    const normalizedObjectives = (mission.objectives || []).map((objective) => ({
      id: objective.id || createObjectiveId(),
      name: objective.name,
      count: objective.count,
      team: objective.team,
      type: objective.type || 'count',
    }));

    setCurrentMission({
      ...mission,
      winPoints: mission.winPoints ?? 0,
      losePoints: mission.losePoints ?? 0,
      duration: typeof mission.duration === 'number' ? mission.duration : 0,
      objectives: normalizedObjectives,
    });
    setSelectedFiles([]);
    setNewVideoUrl('');
    
    // Если у миссии есть существующие изображения, добавляем их в предпросмотр
    if (mission.images && mission.images.length > 0) {
      setPreviewImages(mission.images);
    } else {
      setPreviewImages([]);
    }
    
    setIsModalOpen(true);
  };
  
  // Handler for deleting a mission
  const handleDeleteMission = async (id: string) => {
    const confirmed = await confirm({
      title: 'Delete Mission',
      message: 'Are you sure you want to delete this mission?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    setLoading(true);
    setError('');

    try {
      await deleteMission(id);
      setSuccessMessage('Mission successfully deleted!');
      fetchMissions();
    } catch (err) {
      setError('Failed to delete mission.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handler for file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setSelectedFiles(prevFiles => [...prevFiles, ...newFiles]);
      
      // Создаем URL для предпросмотра
      const newPreviewUrls = newFiles.map(file => URL.createObjectURL(file));
      setPreviewImages(prevUrls => [...prevUrls, ...newPreviewUrls]);
    }
  };
  
  // Handler for removing file
  const handleRemoveFile = (index: number) => {
    // Проверяем, это новый файл или существующее изображение
    const isNewFile = index < selectedFiles.length;
    
    if (isNewFile) {
      // Удаляем файл и его превью
      const newFiles = [...selectedFiles];
      newFiles.splice(index, 1);
      setSelectedFiles(newFiles);
      
      const newPreviews = [...previewImages];
      URL.revokeObjectURL(newPreviews[index]); // Освобождаем URL
      newPreviews.splice(index, 1);
      setPreviewImages(newPreviews);
    } else {
      // Если это существующее изображение, просто удаляем его из превью
      const newPreviews = [...previewImages];
      newPreviews.splice(index, 1);
      setPreviewImages(newPreviews);
      
      // Обновляем список изображений в currentMission, если он существует
      if (currentMission && currentMission.images) {
        const newImages = [...currentMission.images];
        newImages.splice(index - selectedFiles.length, 1);
        setCurrentMission({
          ...currentMission,
          images: newImages
        });
      }
    }
  };
  
  // Handler for adding video
  const handleAddVideo = () => {
    if (!newVideoUrl.trim() || !currentMission) return;
    
    // Проверяем, что это YouTube URL
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/;
    if (!youtubeRegex.test(newVideoUrl)) {
      setError('Please enter a valid YouTube URL');
      return;
    }
    
    const updatedVideos = [...(currentMission.videos || []), newVideoUrl];
    setCurrentMission({
      ...currentMission,
      videos: updatedVideos
    });
    setNewVideoUrl('');
  };

  // Handler for removing video
  const handleRemoveVideo = (index: number) => {
    if (!currentMission) return;

    const updatedVideos = [...(currentMission.videos || [])];
    updatedVideos.splice(index, 1);
    setCurrentMission({
      ...currentMission,
      videos: updatedVideos
    });
  };

  const handleAddObjective = () => {
    if (!currentMission) return;

    const nextObjective: MissionObjectiveDefinition = {
      id: createObjectiveId(),
      name: '',
      count: 1,
      team: 'human',
      type: 'count',
    };

    setCurrentMission({
      ...currentMission,
      objectives: [...(currentMission.objectives || []), nextObjective],
    });
  };

  const handleObjectiveChange = (index: number, field: 'name' | 'count' | 'team' | 'type', value: string) => {
    if (!currentMission) return;

    const objectives = [...(currentMission.objectives || [])];
    if (!objectives[index]) {
      return;
    }

    const updatedObjective = { ...objectives[index] } as MissionObjectiveDefinition;

    if (field === 'name') {
      updatedObjective.name = value;
    } else if (field === 'team') {
      updatedObjective.team = (value as 'human' | 'alien') || 'human';
    } else if (field === 'type') {
      updatedObjective.type = (value as MissionObjectiveType) || 'count';
    } else if (field === 'count') {
      const parsed = value === '' ? 0 : Math.max(0, Number(value));
      updatedObjective.count = Number.isFinite(parsed) ? parsed : 0;
    }

    objectives[index] = updatedObjective;
    setCurrentMission({
      ...currentMission,
      objectives,
    });
  };

  const handleRemoveObjective = (index: number) => {
    if (!currentMission) return;

    const objectives = [...(currentMission.objectives || [])];
    objectives.splice(index, 1);
    setCurrentMission({
      ...currentMission,
      objectives,
    });
  };
  
  // Handler for saving a mission with files
  const handleSaveMissionWithFiles = async () => {
    if (!currentMission) return;
    
    // Form validation
    if (!currentMission.name.trim()) {
      setError('Please enter a mission name.');
      return;
    }
    
    if (currentMission.price < 0) {
      setError('Price cannot be negative.');
      return;
    }

    if (!currentMission.duration || currentMission.duration <= 0) {
      setError('Duration must be greater than zero.');
      return;
    }

    if (currentMission.winPoints === undefined || currentMission.winPoints < 0) {
      setError('Win points cannot be negative.');
      return;
    }

    if (currentMission.losePoints === undefined || currentMission.losePoints < 0) {
      setError('Lose points cannot be negative.');
      return;
    }

    const objectives = currentMission.objectives || [];
    if (objectives.length === 0) {
      setError('Add at least one mission objective.');
      return;
    }

    for (const objective of objectives) {
      if (!objective.name || !objective.name.trim()) {
        setError('Each mission objective needs a name.');
        return;
      }

      if (!objective.count || objective.count <= 0) {
        setError('Objective count must be greater than zero.');
        return;
      }

      if (objective.team !== 'human' && objective.team !== 'alien') {
        setError('Objective team must be human or alien.');
        return;
      }

      if (!objective.type) {
        setError('Objective type is required.');
        return;
      }
    }

    setLoading(true);
    setError('');
    
    try {
      // Создаем FormData для отправки файлов и данных
      const formData = new FormData();
      formData.append('Title', currentMission.name);
      formData.append('Description', currentMission.description);
      formData.append('Price', currentMission.price.toString());
      formData.append('Type', currentMission.gameType === 'AIRSOFT' ? "Airsoft" : "Paintball");
      formData.append('Difficulty', getDifficultyAsString(currentMission.difficulty));
      formData.append('IsActive', (currentMission.status === 'ACTIVE').toString());
      formData.append('WinPoints', currentMission.winPoints?.toString() ?? '0');
      formData.append('LosePoints', currentMission.losePoints?.toString() ?? '0');
      formData.append('IsLiveTrackable', (currentMission.isLiveTrackable ?? false).toString());

      (currentMission.objectives || []).forEach((objective, index) => {
        formData.append(`MissionObjectives[${index}].Name`, objective.name);
        formData.append(`MissionObjectives[${index}].Count`, objective.count.toString());
        formData.append(`MissionObjectives[${index}].Team`, objective.team);
        formData.append(`MissionObjectives[${index}].Type`, objective.type);
      });

      // Добавляем дополнительные поля
      if (currentMission.minPlayers) formData.append('PlayerCountMin', currentMission.minPlayers.toString());
      if (currentMission.maxPlayers) formData.append('PlayerCountMax', currentMission.maxPlayers.toString());
      formData.append('Duration', (currentMission.duration ?? 0).toString());
      
      // Добавляем видео если есть
      if (currentMission.videos && currentMission.videos.length > 0) {
        currentMission.videos.forEach(videoUrl => {
          formData.append('Videos', videoUrl);
        });
      }
      
      // Добавляем тег с идентификатором миссии
      formData.append('Tags', `MISSION-${currentMission.missionId || 'UNKNOWN'}`);
      
      // Добавляем файлы
      selectedFiles.forEach(file => {
        formData.append('ImageFiles', file);
      });
      
      // Если есть существующие изображения, добавляем их в список Images
      if (currentMission.images && currentMission.images.length > 0) {
        currentMission.images.forEach(imageUrl => {
          formData.append('Images', imageUrl);
        });
      }
      
      if (!currentMission.id) {
        // Создание новой миссии
        await createMissionWithFiles(formData);
        setSuccessMessage('Mission successfully created!');
      } else {
        // Обновление существующей миссии
        await updateMissionWithFiles(currentMission.id, formData);
        setSuccessMessage('Mission successfully updated!');
      }
      
      setIsModalOpen(false);
      fetchMissions();
    } catch (err) {
      setError('Failed to save mission. Please check your data.');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function for difficulty name
  const getDifficultyAsString = (level: number): string => {
    switch (level) {
      case 1: return "Easy";
      case 2: return "Medium";
      case 3: return "Hard";
      case 4: return "Expert";
      default: return "Medium";
    }
  };
  
  // Handler for form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (!currentMission) return;

    const { name, value } = e.target;

    if (name === 'duration') {
      const parsed = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
      setCurrentMission({
        ...currentMission,
        duration: parsed,
      });
      return;
    }

    if (name === 'winPoints' || name === 'losePoints') {
      const parsed = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
      setCurrentMission({
        ...currentMission,
        [name]: parsed,
      } as Mission);
      return;
    }

    if (name === 'price') {
      // Handle price input in a more intuitive way
      // Remove leading zeros except if it's just "0"
      let processedValue = value;
      
      // If the value is starting with 0 and has more digits, remove the leading zero
      if (value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
        processedValue = value.replace(/^0+/, '');
      }

      // Handle empty string as zero
      if (value === '') {
        setCurrentMission({
          ...currentMission,
          [name]: 0
        });
      } else {
        // Convert to number for non-empty strings
        const numValue = parseFloat(processedValue);
        if (!isNaN(numValue)) {
          setCurrentMission({
            ...currentMission,
            [name]: numValue
          });
        }
      }
    } else if (name === 'difficulty') {
      // Convert string to number for difficulty field
      setCurrentMission({
        ...currentMission,
        [name]: parseInt(value) || 1
      });
    } else {
      // For other fields just set the value
      setCurrentMission({
        ...currentMission,
        [name]: value
      });
    }
  };
  
  // Search handler
  const handleSearch = () => {
    fetchMissions();
  };
  
  // Statistics
  const activeMissions = missions.filter(mission => mission.status === 'ACTIVE').length;
  const draftMissions = missions.filter(mission => mission.status === 'DRAFT').length;
  const archivedMissions = missions.filter(mission => mission.status === 'ARCHIVED').length;
  
  return (
    <Container>
      <PageHeader>
        <PageTitle>Product Operations</PageTitle>
        <InfoButton title="Product Operations Help" sections={productOperationsInfo} />
      </PageHeader>

      {/* Statistics cards */}
      <MetricsGrid>
        <MetricCard>
          <MetricLabel>Active Missions</MetricLabel>
          <MetricValue>{activeMissions}</MetricValue>
          <MetricFooter>Available for order</MetricFooter>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Draft Missions</MetricLabel>
          <MetricValue>{draftMissions}</MetricValue>
          <MetricFooter>In development</MetricFooter>
        </MetricCard>
        <MetricCard>
          <MetricLabel>Archived Missions</MetricLabel>
          <MetricValue>{archivedMissions}</MetricValue>
          <MetricFooter>Not available for order</MetricFooter>
        </MetricCard>
      </MetricsGrid>
      
      {/* Error and success messages */}
      {error && <ErrorText>{error}</ErrorText>}
      {successMessage && <SuccessText>{successMessage}</SuccessText>}
      
      {/* Search bar */}
      <SearchBar>
        <SearchInput 
          type="text" 
          placeholder="Search missions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <SearchButton onClick={handleSearch}>Search</SearchButton>
        <ActionButton onClick={handleAddMission}>+ New Mission</ActionButton>
      </SearchBar>
      
      {/* Missions table */}
      <DataTable>
        <TableGrid>
          <TableHeaderRow>
            <div>#</div>
            <div>Name</div>
            <div>Description</div>
            <div>Type</div>
            <div>Difficulty</div>
            <div>Price</div>
            <div>Actions</div>
          </TableHeaderRow>
          
          {missions.map((mission, index) => (
            <TableDataRow key={mission.id}>
              <div>{index + 1}</div>
              <div>{mission.name}</div>
              <div>{mission.description.length > 50 
                ? `${mission.description.substring(0, 50)}...` 
                : mission.description}</div>
              <div className={mission.gameType.toLowerCase()}>{mission.gameType}</div>
              <div>{difficultyLevels.find(d => d.value === mission.difficulty)?.label || 'Medium'}</div>
              <div>${mission.price.toFixed(2)}</div>
              <div>
                <ActionButton onClick={() => handleEditMission(mission)}>
                  Edit
                </ActionButton>
                <DeleteButton onClick={() => handleDeleteMission(mission.id)}>
                  Delete
                </DeleteButton>
              </div>
            </TableDataRow>
          ))}
          
          {missions.length === 0 && (
            <TableDataRow>
              <div style={{ gridColumn: 'span 7', textAlign: 'center' }}>
                {loading ? 'Loading missions...' : 'No missions found.'}
              </div>
            </TableDataRow>
          )}
        </TableGrid>
      </DataTable>
      
      {/* Add/Edit mission modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={currentMission && currentMission.id ? 'Edit Mission' : 'New Mission'}
      >
        {currentMission && (
          <>
            <FormRow>
              <FormLabel>Name:</FormLabel>
              <FormInput 
                name="name"
                value={currentMission.name}
                onChange={handleInputChange}
              />
            </FormRow>
            
            <FormRow>
              <FormLabel>Description:</FormLabel>
              <FormTextArea 
                name="description"
                value={currentMission.description}
                onChange={handleInputChange}
              />
            </FormRow>
            
            <FormRow>
              <FormLabel>Game Type:</FormLabel>
              <FormSelect 
                name="gameType"
                value={currentMission.gameType}
                onChange={handleInputChange}
              >
                <option value={gameTypes.AIRSOFT}>Airsoft</option>
                <option value={gameTypes.PAINTBALL}>Paintball</option>
              </FormSelect>
            </FormRow>
            
            <FormRow>
              <FormLabel>Difficulty:</FormLabel>
              <FormSelect 
                name="difficulty"
                value={currentMission.difficulty}
                onChange={handleInputChange}
              >
                {difficultyLevels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </FormSelect>
            </FormRow>
            
            <FormRow>
              <FormLabel>Price:</FormLabel>
              <FormInput 
                type="number"
                name="price"
                value={currentMission.price === 0 && document.activeElement === document.querySelector('input[name="price"]') ? '' : currentMission.price}
                onChange={handleInputChange}
                onFocus={(e) => {
                  if (parseFloat(e.target.value) === 0) {
                    e.target.value = '';
                  }
                }}
                min="0"
                step="100"
              />
            </FormRow>

            <FormRow>
              <FormLabel>Duration (min):</FormLabel>
              <FormInput
                type="number"
                name="duration"
                min="1"
                value={(currentMission.duration ?? 0) === 0 ? '' : currentMission.duration}
                onChange={handleInputChange}
              />
            </FormRow>

            <FormRow>
              <FormLabel>Mission Code:</FormLabel>
              <FormInput 
                name="missionId"
                value={currentMission.missionId}
                onChange={handleInputChange}
              />
            </FormRow>
            
            <FormRow>
              <FormLabel>Status:</FormLabel>
              <FormSelect 
                name="status"
                value={currentMission.status}
                onChange={handleInputChange}
              >
                <option value={missionStatuses.ACTIVE}>Active</option>
                <option value={missionStatuses.DRAFT}>Draft</option>
                <option value={missionStatuses.ARCHIVED}>Archived</option>
              </FormSelect>
            </FormRow>

            <FormRow>
              <FormLabel>Win Points:</FormLabel>
              <FormInput 
                type="number"
                name="winPoints"
                min="0"
                value={(currentMission.winPoints ?? 0) === 0 ? '' : currentMission.winPoints}
                onChange={handleInputChange}
              />
            </FormRow>

            <FormRow>
              <FormLabel>Lose Points:</FormLabel>
              <FormInput
                type="number"
                name="losePoints"
                min="0"
                value={(currentMission.losePoints ?? 0) === 0 ? '' : currentMission.losePoints}
                onChange={handleInputChange}
              />
            </FormRow>

            <FormRow>
              <FormLabel>Live Tracking:</FormLabel>
              <CheckboxContainer>
                <HiddenCheckbox
                  checked={currentMission.isLiveTrackable ?? false}
                  onChange={(e) => setCurrentMission({
                    ...currentMission,
                    isLiveTrackable: e.target.checked
                  })}
                />
                <StyledCheckbox checked={currentMission.isLiveTrackable ?? false} />
                Enable real-time tracking
              </CheckboxContainer>
            </FormRow>

            <SectionTitle>Mission Objectives</SectionTitle>
            <ObjectivesContainer>
              {(currentMission.objectives || []).map((objective, index) => (
                <ObjectiveCard key={objective.id || `objective-${index}`}>
                  <ObjectiveHeader>
                    <div style={{ fontFamily: 'Courier New, monospace', color: '#ffffff' }}>Objective #{index + 1}</div>
                    <RemoveObjectiveButton type="button" onClick={() => handleRemoveObjective(index)}>
                      Remove
                    </RemoveObjectiveButton>
                  </ObjectiveHeader>
                  <ObjectiveFields>
                    <SmallInput
                      name={`objective-name-${index}`}
                      value={objective.name}
                      onChange={(event) => handleObjectiveChange(index, 'name', event.target.value)}
                      placeholder="Name"
                    />
                    <SmallInput
                      type="number"
                      min="1"
                      name={`objective-count-${index}`}
                      value={objective.count === 0 ? '' : objective.count}
                      onChange={(event) => handleObjectiveChange(index, 'count', event.target.value)}
                      placeholder="Count"
                    />
                    <SmallSelect
                      name={`objective-type-${index}`}
                      value={objective.type}
                      onChange={(event) => handleObjectiveChange(index, 'type', event.target.value)}
                    >
                      <option value="count">COUNT</option>
                      <option value="time">TIME</option>
                      <option value="points">POINTS</option>
                    </SmallSelect>
                    <SmallSelect
                      name={`objective-team-${index}`}
                      value={objective.team}
                      onChange={(event) => handleObjectiveChange(index, 'team', event.target.value)}
                    >
                      <option value="human">Human</option>
                      <option value="alien">Alien</option>
                    </SmallSelect>
                  </ObjectiveFields>
                </ObjectiveCard>
              ))}
              <AddObjectiveButton type="button" onClick={handleAddObjective}>
                Add Objective
              </AddObjectiveButton>
            </ObjectivesContainer>
            
            <FormRow>
              <FormLabel>Videos:</FormLabel>
              <div>
                <VideoInput 
                  value={newVideoUrl}
                  onChange={(e) => setNewVideoUrl(e.target.value)}
                  placeholder="https://youtu.be/VIDEO_ID or https://youtube.com/watch?v=VIDEO_ID"
                />
                <AddVideoButton onClick={handleAddVideo}>Add Video</AddVideoButton>
                
                {currentMission.videos && currentMission.videos.length > 0 && (
                  <VideoPreviewContainer>
                    {currentMission.videos.map((videoUrl, index) => (
                      <VideoItem key={index}>
                        <VideoUrl>{videoUrl}</VideoUrl>
                        <RemoveVideoButton onClick={() => handleRemoveVideo(index)}>
                          Remove
                        </RemoveVideoButton>
                      </VideoItem>
                    ))}
                  </VideoPreviewContainer>
                )}
              </div>
            </FormRow>
            
            <FormRow>
              <FormLabel>Images:</FormLabel>
              <FileInput 
                type="file" 
                accept="image/*"
                onChange={handleFileSelect}
                multiple
              />
              
              {previewImages.length > 0 && (
                <ImagePreviewContainer>
                  {previewImages.map((url, index) => (
                    <ImagePreview key={index} url={url}>
                      <RemoveImageButton onClick={() => handleRemoveFile(index)}>
                        X
                      </RemoveImageButton>
                    </ImagePreview>
                  ))}
                </ImagePreviewContainer>
              )}
            </FormRow>
            
            <ButtonContainer>
              <SubmitButton 
                onClick={handleSaveMissionWithFiles}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </SubmitButton>
            </ButtonContainer>
          </>
        )}
      </Modal>
      <ConfirmModal {...modalProps} />
    </Container>
  );
};

export default OperationManagement; 
